import mixpanel, { Dict } from "mixpanel-browser";
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import useUserPreference from "modules/hooks/use-user-preference";
import { useAuth } from "../auth-provider";
import { useOrganization } from "../organization-provider";
import { platform } from "@todesktop/client-core";
import { useUserAttributes } from "../user-attributes-provider";
import { UserAttributeFragment } from "gql/graphql";

export enum MIXPANEL_GROUP_TYPE {
  ORGANIZATION = "ORGANIZATION",
}

export enum PERSON_COUNT_TYPE {
  CONVERSATIONS_VIEWED = "Total Conversations Viewed",

  CONVERSATIONS_INBOX_TOGGLED = "Total Conversations Archived",
  CONVERSATIONS_NOTIFICATIONS_TOGGLED = "Total Conversations Muted",
  MESSAGES_SENT = "Lifetime Messages Sent",
  SEARCHES = "Total Searches",
  MESSAGE_REACTIONS = "Total Message Reactions",
  REMINDER_COUNT = "Count of Reminders",
}

export enum MIXPANEL_EVENT_TYPE {
  MESSAGE_SENT = "Message Sent",
  SIGN_IN = "Signed In",
  SIGN_OUT = "Signed Out",
  SEARCH = "Search",
  CONVERSATION_VIEWED = "Conversation Viewed",
  CONVERSATION_STAR_TOGGLED = "Conversation Starred",
  CONVERSATION_NOTIFICATIONS_TOGGLED = "Muted Conversation",
  CONVERSATION_INBOX_TOGGLED = "Conversation Archived",
  CONVERSATION_INBOX_REMINDER_TOGGLED = "Remind Me Set",
  CONVERSATION_DETAILS_VIEWED = "Viewed Conversation Details",
  CONVERSATION_UNSEEN_TOGGLED = "Mark as Unread",
  CONVERSATION_TRANSLATION_TOGGLED = "Conversation Translated",
  MESSAGE_DETAILS_VIEWED = "Viewed Message Details",
  FILTERED_CONVERSATIONS = "Filtered Conversations",
  MESSAGE_REACTION = "Message Reaction",
  MESSAGE_TRANSLATION_TOGGLED = "Message Translation Toggled",
  LINK = "Clicked Link",
  DISTRIBUTION_GROUP_NOTIFICATIONS_TOGGLED = "Toggled Group Notifcation Setting",
  STARRED_CONTACTS_REORDERED = "Contact Starred Reordered",
  CONTACT_STARRED = "Contact Starred",
  FAILED_TO_REFRESH_AFTER_NETWORK_OUTAGE = "Network outage - failed to refresh",
  NETWORK_OUTAGE_REFRESHED_AFTER_RETRIES = "Network outage - successfully refreshed after retrying",
  USER_PREFERRED_LANGUAGE_SET = "User Language Preference Set",
  MESSAGE_PANEL_RESIZED = "Message Panel Resized",
  LOCK_CONVERSATION_TOGGLED = "Lock Conversation Toggled",
  READ_ONLY_CONVERSATION_TOGGLED = "Read-Only Conversation Toggled",
  CONVERSTION_PRINTED = "Conversation Printed",
  CONVERSTION_EXPORTED = "Conversation Exported",
  TAG_REMOVED = "Tag Removed",
  USER_REMOVED = "User Removed",
  DISTRIBUTION_GROUP_REMOVED = "Group Removed",
  AUTO_SUGGEST_SUBJECT = "Auto Suggested Subject",
  SUMMARIZE_CONVERSATION = "AI Summarized Conversation",
  CONVERSATION_PINNED = "Conversation Pinned",
  QUICK_FILTERS = "Quick-filtered Conversations",
  OUT_OF_OFFICE_SET = "Out of Office Set",
}

interface IMixpanel {
  track: (type: MIXPANEL_EVENT_TYPE, properties?: Dict) => void;
  incrementPersonProperty: (type: PERSON_COUNT_TYPE) => void;
}

const MixpanelContext = React.createContext<IMixpanel>({
  track: () => null,
  incrementPersonProperty: () => null,
});

export interface IMixpanelProps {
  children?: ReactNode;
}

export const MixpanelProvider = ({ children }: IMixpanelProps) => {
  const { me } = useAuth();
  const { stringValue: locale } = useUserPreference({
    userId: me?.id,
    name: "LOCALE",
  });
  const { organization } = useOrganization();
  const { userAttributesById, userAttributeCategoriesById } =
    useUserAttributes();

  const userAttributeValues = useMemo(() => {
    if (!me?.userAttributeIdList?.length) return {};

    const userAttributes = me.userAttributeIdList
      .map((id) => userAttributesById[id])
      .filter(
        (userAttribute): userAttribute is UserAttributeFragment =>
          !!userAttribute,
      );

    const grouped: Record<string, string[]> = {};

    for (const userAttribute of userAttributes) {
      const userAttributeCategory =
        userAttributeCategoriesById[userAttribute.userAttributeCategoryId];
      const categoryName = userAttributeCategory?.name ?? "Unknown Category";
      const attributeName = userAttribute.name ?? "Unknown Attribute";

      if (!grouped[categoryName]) grouped[categoryName] = [];
      grouped[categoryName].push(attributeName);
    }

    return Object.fromEntries(
      Object.entries(grouped).map(([category, attrs]) => [
        category,
        attrs.join(", "),
      ]),
    );
  }, [
    me?.userAttributeIdList,
    userAttributesById,
    userAttributeCategoriesById,
  ]);

  const track = useCallback((type: MIXPANEL_EVENT_TYPE, properties?: Dict) => {
    mixpanel.track(type, properties);
  }, []);

  const incrementPersonProperty = useCallback((type: PERSON_COUNT_TYPE) => {
    mixpanel.people.increment(type);
  }, []);

  useEffect(() => {
    if (!me || !organization) return;
    mixpanel.identify(me.id);
    mixpanel.register({
      Organization: organization.name,
      Callout: me.callout,
      "First Name": me.firstName,
      "Last Name": me.lastName,
    });
    mixpanel.people.set({
      Organization: organization.name,
      Name: `${me.firstName} ${me.lastName}`,
      "First Name": me.firstName,
      "Last Name": me.lastName,
      Callout: me.callout,
      Email: me.email,
      Phone: me.phone,
      Created: me.createTime,
      "Role Type": me.roleType,
      "Preferred Language": locale,
      "Conversation Window %": me.inboxScale
        ? Math.round((1 - me.inboxScale) * 100)
        : undefined,
      "Application Type": platform.todesktop.isDesktopApp() ? "Desktop" : "Web",
      ...userAttributeValues,
    });
    mixpanel.set_group(MIXPANEL_GROUP_TYPE.ORGANIZATION, organization.id);
    mixpanel.get_group(MIXPANEL_GROUP_TYPE.ORGANIZATION, organization.id).set({
      Name: organization.name,
    });
  }, [locale, me, organization, userAttributeValues]);

  const modal = useMemo<IMixpanel>(
    () => ({
      track,
      incrementPersonProperty,
    }),
    [track, incrementPersonProperty],
  );

  return (
    <MixpanelContext.Provider value={modal}>
      {children}
    </MixpanelContext.Provider>
  );
};

export const useMixpanel = () => useContext(MixpanelContext);
